import { create } from 'zustand';
import { 
  createVehicleReceivingForm,
  createVehicleDelivery,
  getVehicleReceivingFormByAssignment,
  getVehicleDeliveryFormByAssignment,
  createVehicleDamage,
} from '../api/supabase';
import { 
  assignmentHybridStore,
  receivingFormHybridStore,
  deliveryFormHybridStore,
  damageHybridStore,
  useNetworkStatus
} from '../services/offline';
import { useAssignmentStore } from './assignmentStore';
import { useVehicleStore } from './vehicleStore';

interface BilingualFormData {
  // Basic Information
  branch: string;
  vehicleId: string;
  licensePlate: string;
  driverName: string;
  driverPhone: string;
  
  // Partner Driver Information (for shared assignments)
  isSharedAssignment?: boolean;
  secondaryDriverName?: string;
  secondaryDriverPhone?: string;
  partnershipType?: 'shared' | 'single';
  
  // Rate Information
  monthlyRate?: number; // Monthly rental rate (primary input for partnerships)
  dailyRate?: number; // Calculated from monthly rate
  
  // Vehicle Delivery Data
  deliveryDate: string;
  deliveryTime: string;
  fuelQuantity: string;
  vehicleNumber: string; // Same as license plate
  makeModelYear: string; // Combined from vehicle table
  deliveryMileage: string;
  cleanlinessExternal: string;
  cleanlinessInternal: string;
  tireCondition: string;
  lightsCondition: string;
  signalsCondition: string;
  jackCondition: string;
  fireExtinguisherCondition: string;
  spareTireCondition: string;
  toolsCondition: string;
  vehicleShapeCondition: string;
  deliveryNotes: string;
  
  // Additional Delivery Fields
  firstAidBox: string;
  repairTools: string;
  vehicleOwnership: string;
  
  // Vehicle Receiving Data  
  receivingDate: string;
  receivingTime: string;
  receivingMileage: string;
  receivingNotes: string;
  
  // Return Condition Assessment
  receivingCleanlinessExternal: string;
  receivingCleanlinessInternal: string;
  receivingTireCondition: string;
  receivingLightsCondition: string;
  receivingSignalsCondition: string;
  receivingJackCondition: string;
  receivingFireExtinguisherCondition: string;
  receivingToolsCondition: string;
  receivingSpareTireCondition: string;
  receivingVehicleShapeCondition: string;
  
  // Signatures
  driverSignature: string;
  secondaryDriverSignature?: string; // For partnership assignments
  companySignature: string;
  date: string;
  
  // Meta data
  formType?: 'delivery' | 'receiving' | 'swap';
  referenceNumber?: string;
  submittedAt?: string;
  
  // Photo arrays (NEW: for assignment_history.delivery_photos and receiving_photos)
  deliveryPhotos?: string[];
  receivingPhotos?: string[];
  
  // Vehicle damage markers
  vehicleDamages?: Array<{
    id: string;
    x: number;
    y: number;
    type: 'scratch' | 'dent' | 'missing' | 'broken';
    severity: 'minor' | 'moderate' | 'major';
    description?: string;
    timestamp: string;
  }>;
}

interface AssignmentFormState {
  loading: boolean;
  error: string | null;
  
  // Form submission functions
  submitDeliveryForm: (formData: BilingualFormData, vehicleId: string, driverId: string, secondaryDriverId?: string | null, isSharedAssignment?: boolean) => Promise<any>;
  submitReceivingForm: (formData: BilingualFormData, vehicleId: string, driverId: string, secondaryDriverId?: string | null) => Promise<any>;
  submitSwapForm: (formData: BilingualFormData, oldVehicleId: string, newVehicleId: string, driverId: string) => Promise<any>;
  
  // Validation functions
  validateAssignmentConstraints: (vehicleId: string, driverId: string, secondaryDriverId?: string | null, isSharedAssignment?: boolean) => Promise<void>;
  validateReceivingConstraints: (vehicleId: string, driverId: string) => Promise<any>;
  
  // Partnership helper functions
  getCurrentMonthDays: () => number;
  calculateDailyRate: (monthlyRate: number) => number;
  generatePartnershipRefNumber: (baseRef: string, isPrimary: boolean) => string;
  cloneFormDataForPartner: (formData: any, secondaryDriverId: string, secondarySignature: string, baseRefNumber: string) => any;
  
  // Integration with assignment workflow
  processVehicleAssignment: (vehicleId: string, driverId: string, formData: BilingualFormData) => Promise<void>;
  processVehicleReturn: (vehicleId: string, driverId: string, formData: BilingualFormData) => Promise<void>;
}

// Helper function to map bilingual conditions to database format
const mapConditionToDb = (condition: string): string => {
  if (condition.includes('ممتاز') || condition.includes('Excellent')) return 'excellent';
  if (condition.includes('جيد') || condition.includes('Good')) return 'good';
  if (condition.includes('متوسط') || condition.includes('Fair')) return 'fair';
  if (condition.includes('سيء') || condition.includes('Poor')) return 'poor';
  return 'good'; // default
};

// Helper function to extract fuel level as percentage
const extractFuelLevel = (fuelStr: string): number => {
  const match = fuelStr.match(/\d+/);
  return match ? parseInt(match[0]) : 50; // default to 50%
};

// PARTNERSHIP HELPER: Get number of days in current month
const getCurrentMonthDays = (): number => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return new Date(year, month + 1, 0).getDate();
};

// PARTNERSHIP HELPER: Calculate daily rate from monthly rate
const calculateDailyRate = (monthlyRate: number): number => {
  return monthlyRate / getCurrentMonthDays();
};

// PARTNERSHIP HELPER: Generate reference number with A/B suffix
const generatePartnershipRefNumber = (baseRef: string, isPrimary: boolean): string => {
  return `${baseRef}-${isPrimary ? 'A' : 'B'}`;
};

// PARTNERSHIP HELPER: Clone form data for secondary driver
const cloneFormDataForPartner = (formData: any, secondaryDriverId: string, secondarySignature: string, baseRefNumber: string): any => {
  return {
    ...formData,
    driver_id: secondaryDriverId,
    driver_signature: secondarySignature,
    reference_number: generatePartnershipRefNumber(baseRefNumber, false),
  };
};

export const useAssignmentFormStore = create<AssignmentFormState>((set, get) => ({
  loading: false,
  error: null,

  /**
   * ⚠️ TERMINOLOGY NOTE ⚠️
   * 
   * This function is named from the COMPANY perspective:
   * "Delivery" = Company delivers vehicle TO driver
   * 
   * From DRIVER perspective: Driver RECEIVES the vehicle
   * 
   * WHEN TO USE: When assigning a vehicle to a driver (start of assignment)
   * 
   * ACTIONS:
   * - Creates assignment record in assignment_history
   * - Sets vehicle status to "work"
   * - Assigns vehicle to driver (current_driver_id)
   * - Creates form in vehicle_delivery table
   * - Supports partner/shared assignments
   * 
   * @param formData - Bilingual form data with vehicle/driver details
   * @param vehicleId - UUID of vehicle being assigned
   * @param driverId - UUID of primary driver
   * @param secondaryDriverId - Optional UUID of secondary/partner driver
   * @param isSharedAssignment - True if this is a shared/partner assignment
   */
  submitDeliveryForm: async (formData: BilingualFormData, vehicleId: string, driverId: string, secondaryDriverId?: string | null, isSwap?: boolean) => {
    const state = get();
    if (state.loading) {
      console.log('🚫 Delivery form submission already in progress, ignoring duplicate request');
      return;
    }

    set({ loading: true, error: null });
    try {
      console.log('🚗 Submitting delivery form (hybrid storage)...', { vehicleId, driverId, isSwap });
      
      // Check for recent duplicate submissions (within last 5 seconds)
      const recentSubmissionKey = `delivery_${vehicleId}_${driverId}`;
      const lastSubmission = localStorage.getItem(recentSubmissionKey);
      const now = Date.now();
      
      if (lastSubmission && (now - parseInt(lastSubmission)) < 5000) {
        console.log('🚫 Preventing duplicate delivery form submission');
        set({ loading: false });
        return;
      }
      
      // Mark this submission
      localStorage.setItem(recentSubmissionKey, now.toString());
      
      // VALIDATION: Check for existing active assignments
      // Skip validation if this is a swap operation (vehicle is already assigned to this driver)
      if (!isSwap) {
        await state.validateAssignmentConstraints(vehicleId, driverId, secondaryDriverId, undefined);
      } else {
        console.log('🔄 Swap operation - skipping assignment validation');
      }
      
      // Create assignment record - trigger will handle vehicle/driver sync automatically!
      const monthlyRate = formData.monthlyRate || 0;
      const dailyRate = monthlyRate > 0 ? calculateDailyRate(monthlyRate) : 0;
      
      // Determine if this is a shared assignment
      const isSharedAssignment = !!secondaryDriverId;
      
      const assignmentData = {
        vehicle_id: vehicleId,
        primary_driver_id: driverId,
        secondary_driver_id: secondaryDriverId || null,
        assignment_date: formData.deliveryDate,
        monthly_rent_rate: monthlyRate,
        daily_rate: dailyRate,
        delivery_photos: formData.deliveryPhotos || [],
        status: 'active' as const,
        is_shared: isSharedAssignment,
        created_at: new Date().toISOString()
      };

      const assignment = await assignmentHybridStore.create(assignmentData);
      console.log('✅ Assignment created with status=active - trigger will auto-sync vehicle/driver!', assignment);
      
      // ✅ NEW: assignment_sync_trigger automatically handles:
      // - Sets vehicle.car_status = 'work'
      // - Sets vehicle.current_driver_id = driverId
      // - Sets driver.current_vehicle_id = vehicleId
      // No manual updates needed!
      
      // Create vehicle delivery form(s) - For partnerships, create TWO forms with A/B suffixes
      const baseReferenceNumber = formData.referenceNumber || `DEL-${Date.now()}`;
      
      // Base form data structure
      const baseDeliveryFormData = {
        assignment_id: assignment.id,
        vehicle_id: vehicleId,
        delivery_date: formData.deliveryDate,
        delivery_time: formData.deliveryTime,
        final_mileage: parseInt(formData.deliveryMileage) || 0,
        fuel_level: extractFuelLevel(formData.fuelQuantity) || 50,
        exterior_condition: mapConditionToDb(formData.cleanlinessExternal),
        interior_condition: mapConditionToDb(formData.cleanlinessInternal),
        tire_condition: mapConditionToDb(formData.tireCondition),
        has_all_keys: formData.jackCondition.includes('جيد') || formData.jackCondition.includes('Good'),
        has_all_documents: true,
        has_spare_wheel: true,
        has_jack_and_tools: true,
        has_damages: false,
        additional_notes: formData.deliveryNotes,
        company_signature: formData.companySignature,
        created_at: new Date().toISOString(),
      };

      if (isSharedAssignment && secondaryDriverId) {
        // PARTNERSHIP: Create TWO delivery forms with A/B suffixes
        console.log('🤝 Creating dual delivery forms for partnership assignment...');
        
        // PRIMARY DRIVER FORM (A suffix)
        const primaryFormData = {
          ...baseDeliveryFormData,
          reference_number: get().generatePartnershipRefNumber(baseReferenceNumber, true),
          driver_id: driverId,
          driver_signature: formData.driverSignature,
          secondary_driver_signature: null
        };
        
        const primaryDeliveryForm = await deliveryFormHybridStore.create(primaryFormData);
        console.log('✅ Primary driver delivery form created (A suffix):', primaryDeliveryForm);
        // Form logging removed - createAssignmentHistory already logs with rich context
        
        // SECONDARY DRIVER FORM (B suffix)
        const secondaryFormData = {
          ...baseDeliveryFormData,
          reference_number: get().generatePartnershipRefNumber(baseReferenceNumber, false),
          driver_id: secondaryDriverId,
          driver_signature: formData.secondaryDriverSignature,
          secondary_driver_signature: null
        };
        
        const secondaryDeliveryForm = await deliveryFormHybridStore.create(secondaryFormData);
        console.log('✅ Secondary driver delivery form created (B suffix):', secondaryDeliveryForm);
        // Form logging removed - createAssignmentHistory already logs with rich context
        
        // Save damages for BOTH forms (same vehicle, same damages)
        if (formData.vehicleDamages && formData.vehicleDamages.length > 0) {
          console.log('🔧 Saving vehicle damage markers for both partnership forms (hybrid):', formData.vehicleDamages);
          
          for (const damage of formData.vehicleDamages) {
            const damageType = (damage.type && typeof damage.type === 'string' && damage.type.trim()) 
              ? damage.type.trim() : 'scratch';
            const damageSeverity = (damage.severity && typeof damage.severity === 'string' && damage.severity.trim()) 
              ? damage.severity.trim() : 'minor';
            
            const baseDamageRecord = {
              damage_type: damageType,
              damage_severity: damageSeverity,
              damage_location: `${damageType} at (${damage.x || 0}, ${damage.y || 0})`,
              damage_description: (damage.description && typeof damage.description === 'string' && damage.description.trim()) 
                ? damage.description.trim() : `${damageType} damage`,
              x_coordinate: Number(damage.x) || 0,
              y_coordinate: Number(damage.y) || 0,
              photo_url: null
            };
            
            // Create damage record for primary form
            await damageHybridStore.create({
              ...baseDamageRecord,
              delivery_form_id: primaryDeliveryForm.id
            });
            
            // Create damage record for secondary form
            await damageHybridStore.create({
              ...baseDamageRecord,
              delivery_form_id: secondaryDeliveryForm.id
            });
          }
          console.log('✅ Vehicle damages saved for both partnership forms (hybrid)');
        }
      } else {
        // SINGLE DRIVER: Create ONE delivery form
        console.log('👤 Creating single delivery form for standard assignment...');
        
        const deliveryFormData = {
          ...baseDeliveryFormData,
          reference_number: baseReferenceNumber,
          driver_id: driverId,
          driver_signature: formData.driverSignature,
          secondary_driver_signature: null
        };

        const deliveryForm = await deliveryFormHybridStore.create(deliveryFormData);
        console.log('✅ Delivery form created (hybrid):', deliveryForm);
        
        // Form submission logging removed - assignment creation already logs with rich context
        
        // Save damage markers for single form
        if (formData.vehicleDamages && formData.vehicleDamages.length > 0) {
          console.log('🔧 Saving vehicle damage markers (hybrid):', formData.vehicleDamages);
          
          for (const damage of formData.vehicleDamages) {
            const damageType = (damage.type && typeof damage.type === 'string' && damage.type.trim()) 
              ? damage.type.trim() : 'scratch';
            const damageSeverity = (damage.severity && typeof damage.severity === 'string' && damage.severity.trim()) 
              ? damage.severity.trim() : 'minor';
            
            const damageRecord = {
              delivery_form_id: deliveryForm.id,
              damage_type: damageType,
              damage_severity: damageSeverity,
              damage_location: `${damageType} at (${damage.x || 0}, ${damage.y || 0})`,
              damage_description: (damage.description && typeof damage.description === 'string' && damage.description.trim()) 
                ? damage.description.trim() : `${damageType} damage`,
              x_coordinate: Number(damage.x) || 0,
              y_coordinate: Number(damage.y) || 0,
              photo_url: null
            };
            
            console.log('🔧 Creating damage record:', damageRecord);
            await damageHybridStore.create(damageRecord);
          }
          console.log('✅ Vehicle damages saved (hybrid)');
        }
      }

      console.log('✅ Delivery form submission completed (offline-capable)');
      // Assignment creation logging handled by createAssignmentHistory in supabase.ts
      
      // IMPORTANT: Sync vehicle/driver status from assignment data if updates failed
      console.log('🔄 Attempting to sync vehicle/driver status from assignment data...');
      try {
        const { useVehicleStore } = require('../stores/vehicleStore');
        const { useDriverStore } = require('../stores/driverStore');
        
        // Get vehicle store and check current status
        const vehicleStore = useVehicleStore.getState();
        const vehicle = vehicleStore.vehicles.find((v: any) => v.id === vehicleId);
        
        if (vehicle && vehicle.carStatus !== 'work') {
          console.log(`🔄 Syncing vehicle ${vehicle.licensePlate} status: ${vehicle.carStatus} -> work`);
          // Update local state directly since hybrid update may have failed
          const updatedVehicles = vehicleStore.vehicles.map((v: any) => 
            v.id === vehicleId 
              ? { ...v, carStatus: 'work', currentDriverId: driverId }
              : v
          );
          useVehicleStore.setState({ vehicles: updatedVehicles });
          console.log(`✅ Vehicle ${vehicle.licensePlate} status synced locally`);
        }
        
        // Update driver status
        const driverStore = useDriverStore.getState();
        const driver = driverStore.drivers.find((d: any) => d.id === driverId);
        
        if (driver && !driver.currentVehicleId) {
          console.log(`🔄 Syncing driver ${driver.name} currentVehicleId: none -> ${vehicleId}`);
          const updatedDrivers = driverStore.drivers.map((d: any) => 
            d.id === driverId 
              ? { ...d, currentVehicleId: vehicleId }
              : d
          );
          useDriverStore.setState({ drivers: updatedDrivers });
          console.log(`✅ Driver ${driver.name} status synced locally`);
        }
        
        // Update secondary driver if needed
        if (secondaryDriverId && isSharedAssignment) {
          const secondaryDriver = driverStore.drivers.find((d: any) => d.id === secondaryDriverId);
          if (secondaryDriver && !secondaryDriver.currentVehicleId) {
            console.log(`🔄 Syncing secondary driver ${secondaryDriver.name} currentVehicleId: none -> ${vehicleId}`);
            const updatedDrivers = driverStore.drivers.map((d: any) => 
              d.id === secondaryDriverId 
                ? { ...d, currentVehicleId: vehicleId }
                : d
            );
            useDriverStore.setState({ drivers: updatedDrivers });
            console.log(`✅ Secondary driver ${secondaryDriver.name} status synced locally`);
          }
        }
      } catch (syncError) {
        console.error('❌ Failed to sync vehicle/driver status:', syncError);
      }
      
      // Refresh assignment history to show the new assignment
      try {
        const assignmentStore = useAssignmentStore.getState();
        await assignmentStore.fetchAssignments();
        console.log('✅ Assignment history refreshed');
      } catch (refreshError) {
        console.warn('⚠️ Failed to refresh assignment history:', refreshError);
      }
      
      set({ loading: false });
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error submitting delivery form:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  /**
   * ⚠️ TERMINOLOGY NOTE ⚠️
   * 
   * This function is named from the COMPANY perspective:
   * "Receiving" = Company receives vehicle FROM driver
   * 
   * From DRIVER perspective: Driver DELIVERS/RETURNS the vehicle
   * 
   * WHEN TO USE: When ending an assignment and returning vehicle (end of assignment)
   * 
   * ACTIONS:
   * - Completes assignment record (status = 'completed')
   * - Sets vehicle status to "available"
   * - Removes vehicle assignment from driver (current_driver_id = null)
   * - Creates form in vehicle_receiving_form table
   * - Handles both regular and partner assignments
   * 
   * @param formData - Bilingual form data with vehicle condition/mileage
   * @param vehicleId - UUID of vehicle being returned
   * @param driverId - UUID of driver returning the vehicle
   */
  submitReceivingForm: async (formData: BilingualFormData, vehicleId: string, driverId: string, secondaryDriverId?: string | null) => {
    const state = get();
    if (state.loading) {
      console.log('🚫 Receiving form submission already in progress, ignoring duplicate request');
      return;
    }

    set({ loading: true, error: null });
    try {
      console.log('🔄 Submitting receiving form (vehicle return - hybrid)...', { vehicleId, driverId });
      
      // Check for recent duplicate submissions (within last 5 seconds)
      const recentSubmissionKey = `receiving_${vehicleId}_${driverId}`;
      const lastSubmission = localStorage.getItem(recentSubmissionKey);
      const now = Date.now();
      
      if (lastSubmission && (now - parseInt(lastSubmission)) < 5000) {
        console.log('🚫 Preventing duplicate receiving form submission');
        set({ loading: false });
        return;
      }
      
      // Mark this submission
      localStorage.setItem(recentSubmissionKey, now.toString());
      
      // VALIDATION: For receiving forms, ensure there is an active assignment to complete
      // This returns the active assignment from the database
      const activeAssignment = await state.validateReceivingConstraints(vehicleId, driverId);
      
      if (!activeAssignment) {
        throw new Error('No active assignment found - validation should have caught this');
      }
      
      console.log('✅ Using active assignment from validation:', activeAssignment.id);
      
      // Calculate daily rent amount from monthly rent rate
      let dailyRentAmount = 0;
      try {
        const vehicleStore = useVehicleStore.getState();
        const vehicle = vehicleStore.vehicles.find((v: any) => v.id === vehicleId);
        
        if (vehicle && vehicle.monthlyRentalRate) {
          // Get number of days in current month
          const now = new Date();
          const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
          dailyRentAmount = parseFloat(vehicle.monthlyRentalRate.toString()) / daysInMonth;
          
          console.log(`💰 Daily rent calculation: AED ${vehicle.monthlyRentalRate}/month ÷ ${daysInMonth} days = AED ${dailyRentAmount.toFixed(2)}/day`);
        } else {
          console.warn('⚠️ Vehicle monthly rental rate not found, using default rent amount of 0');
        }
      } catch (error) {
        console.error('❌ Error calculating daily rent amount:', error);
      }
      
      // Check if this is a partnership assignment
      const isSharedAssignment = activeAssignment?.is_shared || false;
      const partnerDriverId = activeAssignment?.secondary_driver_id || secondaryDriverId;
      
      // Create vehicle receiving form(s) - For partnerships, create TWO forms with A/B suffixes
      const baseReferenceNumber = formData.referenceNumber || `RCV-${Date.now()}`;
      let receivingFormId: string | null = null; // Track the receiving form UUID
      
      // Base form data structure
      const baseReceivingFormData = {
        assignment_id: activeAssignment?.id || null,
        vehicle_id: vehicleId,
        assignment_date: formData.receivingDate,
        assignment_time: formData.receivingTime,
        rent_amount: dailyRentAmount,
        current_mileage: parseInt(formData.receivingMileage) || 0,
        exterior_condition: mapConditionToDb(formData.receivingCleanlinessExternal),
        interior_condition: mapConditionToDb(formData.receivingCleanlinessInternal),
        tire_condition: mapConditionToDb(formData.receivingTireCondition),
        has_damages: false,
        has_spare_key: formData.receivingJackCondition.includes('جيد') || formData.receivingJackCondition.includes('Good'),
        has_documents: true,
        has_first_aid_kit: true,
        has_fire_extinguisher: true,
        has_warning_triangle: true,
        receiving_notes: formData.receivingNotes,
        admin_signature: formData.companySignature || null,
        staff_name: 'Fleet Manager',
        created_at: new Date().toISOString()
      };

      if (isSharedAssignment && partnerDriverId) {
        // PARTNERSHIP: Create TWO receiving forms with A/B suffixes
        console.log('🤝 Creating dual receiving forms for partnership assignment...');
        
        // PRIMARY DRIVER FORM (A suffix)
        const primaryFormData = {
          ...baseReceivingFormData,
          reference_number: get().generatePartnershipRefNumber(baseReferenceNumber, true),
          driver_id: driverId,
          driver_signature: formData.driverSignature || null,
          secondary_driver_signature: null
        };
        
        const primaryReceivingForm = await receivingFormHybridStore.create(primaryFormData);
        console.log('✅ Primary driver receiving form created (A suffix):', primaryReceivingForm);
        receivingFormId = primaryReceivingForm.id; // Store the UUID
        
        // LOG ACTIVITY: Track primary driver receiving form submission
        try {
          const { logFormSubmission } = require('../api/activityLog');
          await logFormSubmission({
            form_id: primaryReceivingForm.id,
            form_type: 'receiving_form',
            assignment_id: activeAssignment?.id || '',
            vehicle_id: vehicleId,
            driver_id: driverId,
            odometer: parseInt(formData.receivingMileage) || undefined,
          });
          console.log('✅ Primary receiving form submission logged');
        } catch (logError) {
          console.error('⚠️ Failed to log primary receiving form submission:', logError);
        }
        
        // SECONDARY DRIVER FORM (B suffix)
        const secondaryFormData = {
          ...baseReceivingFormData,
          reference_number: get().generatePartnershipRefNumber(baseReferenceNumber, false),
          driver_id: partnerDriverId,
          driver_signature: formData.secondaryDriverSignature || null,
          secondary_driver_signature: null
        };
        
        const secondaryReceivingForm = await receivingFormHybridStore.create(secondaryFormData);
        console.log('✅ Secondary driver receiving form created (B suffix):', secondaryReceivingForm);
        
        // LOG ACTIVITY: Track secondary driver receiving form submission
        try {
          const { logFormSubmission } = require('../api/activityLog');
          await logFormSubmission({
            form_id: secondaryReceivingForm.id,
            form_type: 'receiving_form',
            assignment_id: activeAssignment?.id || '',
            vehicle_id: vehicleId,
            driver_id: partnerDriverId,
            odometer: parseInt(formData.receivingMileage) || undefined,
          });
          console.log('✅ Secondary receiving form submission logged');
        } catch (logError) {
          console.error('⚠️ Failed to log secondary receiving form submission:', logError);
        }
        
        // Save damages for BOTH forms (same vehicle, same damages)
        if (formData.vehicleDamages && formData.vehicleDamages.length > 0) {
          console.log('🔧 Saving vehicle damage markers for both partnership forms (hybrid):', formData.vehicleDamages);
          
          for (const damage of formData.vehicleDamages) {
            const damageType = (damage.type && typeof damage.type === 'string' && damage.type.trim()) 
              ? damage.type.trim() : 'scratch';
            const damageSeverity = (damage.severity && typeof damage.severity === 'string' && damage.severity.trim()) 
              ? damage.severity.trim() : 'minor';
            
            const baseDamageData: any = {
              damage_type: damageType,
              damage_severity: damageSeverity,
              damage_location: `${damageType} at (${damage.x || 0}, ${damage.y || 0})`,
              damage_description: (damage.description && typeof damage.description === 'string' && damage.description.trim()) 
                ? damage.description.trim() : `${damageType} damage`,
              x_coordinate: Number(damage.x) || 0,
              y_coordinate: Number(damage.y) || 0,
              photo_url: null
            };
            
            // Create damage record for primary form
            await damageHybridStore.create({
              ...baseDamageData,
              receiving_form_id: primaryReceivingForm.id
            });
            
            // Create damage record for secondary form
            await damageHybridStore.create({
              ...baseDamageData,
              receiving_form_id: secondaryReceivingForm.id
            });
          }
          console.log('✅ Vehicle damages saved for both partnership forms (hybrid)');
        }
      } else {
        // SINGLE DRIVER: Create ONE receiving form
        console.log('👤 Creating single receiving form for standard assignment...');
        
        const receivingFormData = {
          ...baseReceivingFormData,
          reference_number: baseReferenceNumber,
          driver_id: driverId,
          driver_signature: formData.driverSignature || null,
          secondary_driver_signature: null
        };

        const receivingForm = await receivingFormHybridStore.create(receivingFormData);
        console.log('✅ Receiving form created (hybrid):', receivingForm);
        receivingFormId = receivingForm.id; // Store the UUID
        
        // LOG ACTIVITY: Track single receiving form submission
        try {
          const { logFormSubmission } = require('../api/activityLog');
          await logFormSubmission({
            form_id: receivingForm.id,
            form_type: 'receiving_form',
            assignment_id: activeAssignment?.id || '',
            vehicle_id: vehicleId,
            driver_id: driverId,
            odometer: parseInt(formData.receivingMileage) || undefined,
          });
          console.log('✅ Receiving form submission logged');
        } catch (logError) {
          console.error('⚠️ Failed to log receiving form submission:', logError);
        }

        // Save damage markers for single form
        if (formData.vehicleDamages && formData.vehicleDamages.length > 0 && receivingForm?.id) {
          console.log('🔧 Saving vehicle damage markers (hybrid):', formData.vehicleDamages);
          
          for (const damage of formData.vehicleDamages) {
            const damageType = (damage.type && typeof damage.type === 'string' && damage.type.trim()) 
              ? damage.type.trim() : 'scratch';
            const damageSeverity = (damage.severity && typeof damage.severity === 'string' && damage.severity.trim()) 
              ? damage.severity.trim() : 'minor';
            
            const damageData: any = {
              receiving_form_id: receivingForm.id,
              damage_type: damageType,
              damage_severity: damageSeverity,
              damage_location: `${damageType} at (${damage.x || 0}, ${damage.y || 0})`,
              damage_description: (damage.description && typeof damage.description === 'string' && damage.description.trim()) 
                ? damage.description.trim() : `${damageType} damage`,
              x_coordinate: Number(damage.x) || 0,
              y_coordinate: Number(damage.y) || 0,
              photo_url: null
            };
            
            console.log('🔧 Creating damage record:', damageData);
            await damageHybridStore.create(damageData);
          }
          console.log('✅ Vehicle damages saved (hybrid)');
        }
      }

      // Update assignment to pending_settlement (will be completed after monthly settlement is approved)
      if (activeAssignment && receivingFormId) {
        // DIRECT DATABASE UPDATE: Assignment may not be in hybrid storage, so update database directly
        console.log('🔧 Updating assignment to pending_settlement in database...');
        const supabaseClient = require('../api/supabase').supabase;
        const assignmentUpdateResult = await supabaseClient
          .from('assignment_history')
          .update({ 
            status: 'pending_settlement',
            return_date: formData.receivingDate,
            receiving_form_id: receivingFormId, // Use the UUID, not reference number!
            receiving_photos: formData.receivingPhotos || [],
            updated_at: new Date().toISOString()
          })
          .eq('id', activeAssignment.id);
        
        if (assignmentUpdateResult.error) {
          console.error('❌ Assignment pending_settlement update failed:', assignmentUpdateResult.error);
          throw new Error(`Failed to update assignment: ${assignmentUpdateResult.error.message}`);
        } else {
          console.log('✅ Assignment marked as pending_settlement in database');
        }
        
        // LOG ACTIVITY: Track assignment status change
        try {
          const { logAssignmentStatusChange } = require('../api/activityLog');
          await logAssignmentStatusChange({
            assignment_id: activeAssignment.id,
            old_status: 'active',
            new_status: 'pending_settlement',
          });
          console.log('✅ Assignment status change logged');
        } catch (logError) {
          console.error('⚠️ Failed to log assignment status change:', logError);
        }
        
        // ✅ Migration 006: assignment_sync_trigger automatically handles vehicle/driver status
        // When assignment changes to 'pending_settlement':
        //   - Trigger clears driver.current_vehicle_id (driver can take new vehicle)
        //   - Trigger clears vehicle.current_driver_id (vehicle becomes available)
        //   - Trigger sets vehicle.car_status = 'available'
        // Settlement is processed at month-end independent of new assignments
        // No manual updates needed - trigger handles everything!
      }

      console.log('✅ Receiving form submission completed (offline-capable)');
      
      // Refresh assignment history to show the updated assignment
      try {
        const assignmentStore = useAssignmentStore.getState();
        await assignmentStore.fetchAssignments();
        console.log('✅ Assignment history refreshed');
      } catch (refreshError) {
        console.warn('⚠️ Failed to refresh assignment history:', refreshError);
      }
      
      set({ loading: false });
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error submitting receiving form:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  submitSwapForm: async (formData: BilingualFormData, oldVehicleId: string, newVehicleId: string, driverId: string) => {
    set({ loading: true, error: null });
    try {
      console.log('🔄 Submitting swap form (hybrid)...', { oldVehicleId, newVehicleId, driverId });
      
      // Import required stores for unassignment
      const { useVehicleStore } = require('../stores/vehicleStore');
      const { useDriverStore } = require('../stores/driverStore');
      
      // Step 1: Process return of old vehicle (creates receiving form and unassigns)
      console.log('📥 Step 1: Processing return of old vehicle...');
      await get().submitReceivingForm({
        ...formData,
        receivingDate: formData.date,
        receivingTime: new Date().toTimeString().split(' ')[0].substring(0, 5),
        receivingMileage: formData.deliveryMileage, // Final mileage of returned vehicle
      }, oldVehicleId, driverId);

      // Step 2: Explicitly unassign the old vehicle and driver to clear the relationship
      console.log('🔓 Step 2: Explicitly unassigning old vehicle and driver...');
      const vehicleStore = useVehicleStore.getState();
      const driverStore = useDriverStore.getState();
      
      try {
        await Promise.all([
          vehicleStore.unassignVehicle(oldVehicleId),
          driverStore.unassignDriver(driverId)
        ]);
        console.log('✅ Old vehicle and driver unassigned successfully');
      } catch (unassignError) {
        console.warn('⚠️ Unassignment had issues but continuing:', unassignError);
        // Continue anyway - the receiving form should have handled this
      }

      // Step 3: Small delay to ensure database updates are processed
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 4: Process delivery of new vehicle (creates delivery form and assigns)
      console.log('🚚 Step 3: Processing delivery of new vehicle...');
      await get().submitDeliveryForm({
        ...formData,
        deliveryDate: formData.date,
        deliveryTime: new Date().toTimeString().split(' ')[0].substring(0, 5),
        deliveryMileage: formData.receivingMileage, // Initial mileage of new vehicle
      }, newVehicleId, driverId);

      console.log('✅ Vehicle swap completed successfully (hybrid storage)');
      set({ loading: false });
      
      return { message: 'Vehicle swap completed successfully (offline-capable)' };
    } catch (error: any) {
      console.error('❌ Error submitting swap form:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  processVehicleAssignment: async (vehicleId: string, driverId: string, formData: BilingualFormData) => {
    // This is a convenience method that calls submitDeliveryForm
    return await get().submitDeliveryForm(formData, vehicleId, driverId, null, false);
  },

  processVehicleReturn: async (vehicleId: string, driverId: string, formData: BilingualFormData) => {
    // This is a convenience method that calls submitReceivingForm
    return await get().submitReceivingForm(formData, vehicleId, driverId);
  },

  validateAssignmentConstraints: async (vehicleId: string, driverId: string, secondaryDriverId?: string | null, isSharedAssignment?: boolean) => {
    console.log('🔍 Validating assignment constraints using status-based validation...', { vehicleId, driverId, secondaryDriverId, isSharedAssignment });
    
    try {
      // Import required stores
      const { useVehicleStore } = require('../stores/vehicleStore');
      const { useDriverStore } = require('../stores/driverStore');
      
      // Get current state
      const vehicleState = useVehicleStore.getState();
      const driverState = useDriverStore.getState();
      
      // Ensure data is loaded
      await Promise.all([
        vehicleState.fetchVehicles(),
        driverState.fetchDrivers()
      ]);
      
      // Check vehicle availability
      const vehicle = vehicleState.vehicles.find((v: any) => v.id === vehicleId);
      if (!vehicle) {
        throw new Error(`Vehicle not found: ${vehicleId}`);
      }

      // For shared assignments, vehicle can be in 'work' status if it's already shared
      // For regular assignments, vehicle must be 'available'
      if (!isSharedAssignment && vehicle.carStatus !== 'available') {
        const currentDriver = vehicle.currentDriverId ? driverState.drivers.find((d: any) => d.id === vehicle.currentDriverId) : null;
        const currentDriverName = currentDriver?.name || 'Unknown Driver';
        throw new Error(`Vehicle ${vehicle.licensePlate} is not available (Status: ${vehicle.carStatus}). ${vehicle.currentDriverId ? `Currently assigned to: ${currentDriverName}` : ''}`);
      } else if (isSharedAssignment && vehicle.carStatus === 'work') {
        console.log(`⚠️ Vehicle ${vehicle.licensePlate} is in use but allowing for shared assignment`);
      } else if (isSharedAssignment && vehicle.carStatus !== 'available' && vehicle.carStatus !== 'work') {
        throw new Error(`Vehicle ${vehicle.licensePlate} is not available for shared assignment (Status: ${vehicle.carStatus})`);
      }
      
      // Check primary driver availability
      const driver = driverState.drivers.find((d: any) => d.id === driverId);
      if (!driver) {
        throw new Error(`Primary driver not found: ${driverId}`);
      }
      
      console.log(`🔍 Primary driver ${driver.name} current status:`, {
        status: driver.status,
        currentVehicleId: driver.currentVehicleId,
        assignedVehicleId: driver.assigned_vehicle_id,
        assignedVehiclePlate: driver.assigned_vehicle_plate
      });
      
      if (driver.status !== 'active') {
        throw new Error(`Primary driver ${driver.name} is not active (Status: ${driver.status})`);
      }
      
      // Check if driver is already assigned to a vehicle
      if (driver.currentVehicleId) {
        if (!isSharedAssignment) {
          // For regular assignments, prevent multiple assignments
          const currentVehicle = vehicleState.vehicles.find((v: any) => v.id === driver.currentVehicleId);
          const currentVehiclePlate = currentVehicle?.licensePlate || 'Unknown Vehicle';
          
          // Only allow if it's the same vehicle (re-assignment to same vehicle)
          if (driver.currentVehicleId !== vehicleId) {
            const targetVehicle = vehicleState.vehicles.find((v: any) => v.id === vehicleId);
            const targetVehiclePlate = targetVehicle?.licensePlate || 'Unknown Vehicle';
            throw new Error(`Primary driver ${driver.name} is already assigned to vehicle: ${currentVehiclePlate}. Cannot assign to different vehicle: ${targetVehiclePlate}. Please return the current vehicle first.`);
          } else {
            console.log(`⚠️ Driver ${driver.name} is being re-assigned to the same vehicle: ${currentVehiclePlate}`);
          }
        } else if (driver.currentVehicleId !== vehicleId) {
          const currentVehicle = vehicleState.vehicles.find((v: any) => v.id === driver.currentVehicleId);
          const currentVehiclePlate = currentVehicle?.licensePlate || 'Unknown Vehicle';
          const targetVehicle = vehicleState.vehicles.find((v: any) => v.id === vehicleId);
          const targetVehiclePlate = targetVehicle?.licensePlate || 'Unknown Vehicle';
          throw new Error(`Primary driver ${driver.name} is already assigned to vehicle: ${currentVehiclePlate}. Cannot assign to different vehicle: ${targetVehiclePlate} in shared assignment.`);
        } else {
          console.log(`⚠️ Driver ${driver.name} is already assigned to the same vehicle - allowing shared assignment`);
        }
      }
      
      // 🔐 PARTNERSHIP VALIDATION: Enhanced checks for secondary driver
      if (secondaryDriverId) {
        console.log('🤝 Validating partnership assignment...', {
          primaryDriverId: driverId,
          secondaryDriverId,
          vehicleId
        });
        
        // ✅ RULE 1: Primary cannot equal secondary (CHECK FIRST)
        if (driverId === secondaryDriverId) {
          throw new Error(
            '❌ PRIMARY AND SECONDARY DRIVERS CANNOT BE THE SAME PERSON\\n' +
            'السائق الأساسي والثانوي لا يمكن أن يكونا نفس الشخص\\n\\n' +
            'Please select a different driver as partner.'
          );
        }
        
        // ✅ RULE 2: Find secondary driver
        const secondaryDriver = driverState.drivers.find((d: any) => d.id === secondaryDriverId);
        if (!secondaryDriver) {
          throw new Error(
            `❌ SECONDARY DRIVER NOT FOUND\\n` +
            `Driver ID: ${secondaryDriverId}\\n` +
            `Please refresh and try again.`
          );
        }
        
        console.log(`🔍 Partnership Validation - Secondary driver: ${secondaryDriver.name}`, {
          status: secondaryDriver.status,
          currentVehicleId: secondaryDriver.currentVehicleId
        });
        
        // ✅ RULE 3: Secondary driver must be active
        if (secondaryDriver.status !== 'active') {
          throw new Error(
            `❌ SECONDARY DRIVER IS NOT ACTIVE\\n` +
            `Driver: ${secondaryDriver.name}\\n` +
            `Status: ${secondaryDriver.status}\\n\\n` +
            `Only active drivers can be assigned to vehicles.\\n` +
            `السائق الثانوي ${secondaryDriver.name} غير نشط`
          );
        }
        
        // ✅ RULE 4: Secondary driver must NOT have active assignment
        if (secondaryDriver.currentVehicleId) {
          const currentVehicle = vehicleState.vehicles.find((v: any) => v.id === secondaryDriver.currentVehicleId);
          const currentVehiclePlate = currentVehicle?.licensePlate || 'Unknown Vehicle';
          
          throw new Error(
            `❌ SECONDARY DRIVER ALREADY ASSIGNED\\n` +
            `Driver: ${secondaryDriver.name}\\n` +
            `Currently assigned to: ${currentVehiclePlate}\\n\\n` +
            `TO ADD AS PARTNER:\\n` +
            `1. Complete current assignment for ${currentVehiclePlate}\\n` +
            `2. Submit receiving form\\n` +
            `3. Then create new partnership assignment\\n\\n` +
            `السائق الثانوي ${secondaryDriver.name} لديه مهمة نشطة\\n` +
            `مخصص حاليًا لـ: ${currentVehiclePlate}`
          );
        }
        
        // ✅ RULE 5: Check database for hidden conflicts
        console.log('🔍 Checking database for secondary driver conflicts...');
        const { getAssignmentHistory } = require('../api/supabase');
        const allAssignments = await getAssignmentHistory();
        
        const secondaryDriverActiveAssignment = allAssignments.find((assignment: any) => 
          (assignment.primary_driver_id === secondaryDriverId || 
           assignment.secondary_driver_id === secondaryDriverId) &&
          assignment.status === 'active'
        );
        
        if (secondaryDriverActiveAssignment) {
          const conflictVehicle = vehicleState.vehicles.find((v: any) => 
            v.id === secondaryDriverActiveAssignment.vehicle_id
          );
          const conflictVehiclePlate = conflictVehicle?.licensePlate || 'Unknown Vehicle';
          
          throw new Error(
            `❌ SECONDARY DRIVER HAS ACTIVE ASSIGNMENT IN DATABASE\\n` +
            `Driver: ${secondaryDriver.name}\\n` +
            `Vehicle: ${conflictVehiclePlate}\\n` +
            `Assignment ID: ${secondaryDriverActiveAssignment.id}\\n\\n` +
            `This assignment exists in the database but may not be synced locally.\\n` +
            `Please complete this assignment first before creating a new one.\\n\\n` +
            `السائق الثانوي ${secondaryDriver.name} لديه مهمة نشطة في قاعدة البيانات`
          );
        }
        
        console.log(`✅ Partnership validation passed - Both drivers available for assignment`);
      }

      // Additional validation: Check for duplicate active assignments
      // This prevents creating multiple active assignments for the same driver-vehicle combination
      const { getAssignmentHistory } = require('../api/supabase');
      const existingAssignments = await getAssignmentHistory();
      
      const duplicatePrimaryAssignment = existingAssignments.find((assignment: any) => 
        assignment.vehicle_id === vehicleId && 
        assignment.primary_driver_id === driverId && 
        assignment.status === 'active'
      );
      
      if (duplicatePrimaryAssignment) {
        throw new Error(`Primary driver already has an active assignment for this vehicle (Assignment ID: ${duplicatePrimaryAssignment.id})`);
      }
      
      if (secondaryDriverId) {
        const duplicateSecondaryAssignment = existingAssignments.find((assignment: any) => 
          assignment.vehicle_id === vehicleId && 
          assignment.secondary_driver_id === secondaryDriverId && 
          assignment.status === 'active'
        );
        
        if (duplicateSecondaryAssignment) {
          throw new Error(`Secondary driver already has an active assignment for this vehicle (Assignment ID: ${duplicateSecondaryAssignment.id})`);
        }
      }
      
      console.log('✅ Status-based validation passed');
    } catch (error: any) {
      console.error('❌ Status-based validation failed:', error.message);
      throw error;
    }
  },

  validateReceivingConstraints: async (vehicleId: string, driverId: string) => {
    console.log('🔍 Validating receiving constraints using status-based validation...', { vehicleId, driverId });
    
    try {
      // Import required stores
      const { useVehicleStore } = require('../stores/vehicleStore');
      const { useDriverStore } = require('../stores/driverStore');
      
      // Get current state
      const vehicleState = useVehicleStore.getState();
      const driverState = useDriverStore.getState();
      
      // Ensure data is loaded
      await Promise.all([
        vehicleState.fetchVehicles(),
        driverState.fetchDrivers()
      ]);
      
      // Check vehicle exists
      const vehicle = vehicleState.vehicles.find((v: any) => v.id === vehicleId);
      if (!vehicle) {
        throw new Error(`Vehicle not found: ${vehicleId}`);
      }
      
      // Check driver exists
      const driver = driverState.drivers.find((d: any) => d.id === driverId);
      if (!driver) {
        throw new Error(`Driver not found: ${driverId}`);
      }

      // CRITICAL FIX: Check for active assignment FIRST, not vehicle status
      // Vehicle status may have already changed, but active assignment is the source of truth
      const { getAssignmentHistory } = require('../api/supabase');
      const assignments = await getAssignmentHistory();
      const activeAssignment = assignments.find((a: any) => 
        a.vehicle_id === vehicleId && 
        a.primary_driver_id === driverId && 
        a.status === 'active'
      );

      console.log('🔍 Checking driver-vehicle assignment sync:', {
        driverName: driver.name,
        driverCurrentVehicle: driver.currentVehicleId,
        vehiclePlate: vehicle.licensePlate,
        vehicleCurrentDriver: vehicle.currentDriverId,
        vehicleStatus: vehicle.carStatus,
        expectedVehicleId: vehicleId,
        expectedDriverId: driverId,
        hasActiveAssignment: !!activeAssignment
      });

      console.log('🔍 Found active assignment in database:', activeAssignment ? 'Yes' : 'No');

      // NEW VALIDATION LOGIC: Active assignment is the source of truth
      // Don't rely on vehicle status or local sync state - they may be outdated
      
      if (!activeAssignment) {
        // No active assignment found - this is the only real blocker
        console.error('❌ No active assignment found for this driver-vehicle pair');
        throw new Error(
          `No active assignment found for driver ${driver.name} and vehicle ${vehicle.licensePlate}. ` +
          `Cannot submit receiving form without an active assignment.`
        );
      }
      
      // Active assignment exists - validate it's the right one
      console.log('✅ Active assignment found:', {
        assignmentId: activeAssignment.id,
        assignmentDate: activeAssignment.assignment_date,
        status: activeAssignment.status
      });
      
      // Optional: Warn about sync issues but DON'T block
      if (driver.currentVehicleId !== vehicleId || vehicle.currentDriverId !== driverId) {
        console.warn(`⚠️ Local data out of sync but proceeding due to confirmed active assignment:
          - Driver ${driver.name} local vehicle: ${driver.currentVehicleId || 'none'} (expected: ${vehicleId})
          - Vehicle ${vehicle.licensePlate} local driver: ${vehicle.currentDriverId || 'none'} (expected: ${driverId})
          - Vehicle status: ${vehicle.carStatus} (may have changed)
          This is OK - active assignment in database is the source of truth.`);
      }
      
      console.log('✅ Receiving form validation passed - active assignment confirmed');
      
      // Return the active assignment so it can be used for the update
      return activeAssignment;
    } catch (error: any) {
      console.error('❌ Status-based receiving validation failed:', error.message);
      throw error;
    }
  },

  // PARTNERSHIP HELPER METHODS
  
  getCurrentMonthDays: (): number => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    return new Date(year, month + 1, 0).getDate();
  },

  calculateDailyRate: (monthlyRate: number): number => {
    return monthlyRate / get().getCurrentMonthDays();
  },

  generatePartnershipRefNumber: (baseRef: string, isPrimary: boolean): string => {
    return `${baseRef}-${isPrimary ? 'A' : 'B'}`;
  },

  cloneFormDataForPartner: (formData: any, secondaryDriverId: string, secondarySignature: string, baseRefNumber: string): any => {
    return {
      ...formData,
      driver_id: secondaryDriverId,
      driver_signature: secondarySignature,
      reference_number: get().generatePartnershipRefNumber(baseRefNumber, false),
    };
  },
}));